//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useRef, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import VideocamIcon from '@material-ui/icons/Videocam';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { BASE_URL, PROFILE_VIDEO_OVERVIEW, UPDATE_DELETE_VIDEO, RECORD_UPLOAD_VIDEO, CONFIRM_DIALOG_MESSAGE_CHANGE_JS_PROFILE_TO_DRAFT } from '../../common/constants';
import { convertFileSize } from '../../common/fileHelper';
import { getVideoOverviewDurationTextBySeconds } from '../../common/videoHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { changeProfileDraft } from '../../common/services/myProfileServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import VideoOverviewBlock from './VideoOverviewBlock';
import VideoOverviewDialog from './VideoOverviewDialog';
import AfitdLink from '../../common/elemental/AfitdLink';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import FieldLabel from './FieldLabel';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { isVideoExist, isVideoProcessing } = params;

    const videoBlockDashedBorderStyles = {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '140px',
        margin: '0 0 63px 0',
        borderRadius: '4px',
        padding: '12px 18px 23px 20px',
        border: '1px dashed #D5D7DD',
    };

    const videoBlockDefaultStyles = {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        // height: '140px',
        height: 'fit-content',
        margin: '0 0 63px 0',
        borderRadius: '4px',
        padding: '12px 18px 23px 20px',
        border: '1px solid #D5D7DD',
    };

    const videoBlockStyles = isVideoExist ? videoBlockDefaultStyles : videoBlockDashedBorderStyles;

    return makeStyles(theme => ({
        root: {
            backgroundColor: '#FFFFFF',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
        },
        titleContainer: { padding: '0 0 19px 0' },
        recordButtonContainer: {},
        editButton: { color: '#FC821F', padding: '0 5px 19px 0' },
        hintContainer: {
            lineHeight: '24px',
            padding: '4px 0 18px 0',
            letterSpacing: '-0.1px',
        },
        videoBlock: { ...videoBlockStyles },
        videocamIcon: {
            color: '#F0F2F6',
        },
        videoPlaceholder: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
        existingVideoInfoContainer: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
        },
        existingVideoNameSizeContainer: {
            width: '100%',
            display: 'flex',
        },
        existingVideoFileSizeText: {
            color: '#9B9E9C',
            marginLeft: '5px',
        },
        existingVideoDurationText: {
            color: '#9B9E9C',
        },
        resourceCenterLink: {
            textDecoration: 'underline',
        },
        videoExampleLink: {
            textDecoration: 'underline',
        },
        processingContainer: {
            marginBottom: '20px',
            marginTop: '20px',
            backgroundColor: 'black',
            color: 'white',
            padding: '30px',
            textAlign: 'center',
            height: '350px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '22px',
            [theme.breakpoints.down('md')]: {
                padding: '20px',
                fontSize: '20px',
            },
            [theme.breakpoints.down('sm')]: {
                fontSize: '11px',
            },
            [theme.breakpoints.down('xs')]: {
                fontSize: '14px',
            },
        },
        infoIcon: {
            fontSize: '100px',
            [theme.breakpoints.down('md')]: {
                fontSize: '80px',
            },
            [theme.breakpoints.down('sm')]: {
                fontSize: '50px',
            },
            [theme.breakpoints.down('xs')]: {
                fontSize: '30px',
            },
        },
        recordVideoLink: {
            color: isVideoProcessing ? 'grey' : '',
            '&:hover': {
                textDecoration: isVideoProcessing ? 'none' : '',
            },
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        editDialogOpen,
        setEditDialogOpen,
        makeProfilePublicDialog,
        setMakeProfilePublicDialog,
        onConfirmChangeProfileType,
        hideThumbnail,
        setHideThumbnail,
        isPlayedVideo,
        setIsPlayedVideo,
        userAvatar,
        isVideoProcessing,
    } = params;
    const { data = {} } = props;

    const isProfilePublic = _get(data, 'isPublic');
    const isVideoExist = videoExists(props);
    const recordVideoLinkLabel = isVideoExist ? UPDATE_DELETE_VIDEO : RECORD_UPLOAD_VIDEO;

    const defns = {
        titleText: {
            variant: 'h6',
            clsLightBold: true,
            value: PROFILE_VIDEO_OVERVIEW,
        },
        hintText: {
            variant: 'body2',
            display: 'inline',
        },
        resourceCenterLink: {
            display: 'inline',
            bold: false,
            href: `${BASE_URL}/resource-center/5`,
            target: '_blank',
        },
        videoExampleLink: {
            display: 'inline',
            bold: false,
            href: `/home/p/1`,
            target: '_blank',
        },
        recordVideoText: {
            variant: 'body1',
            clsGrayColor: true,
            value: 'Record Video',
        },
        videocamIcon: {
            style: { fontSize: '70px' },
        },
        recordVideoLink: {
            label: recordVideoLinkLabel,
            onClick: !isVideoProcessing
                ? () => {
                      if (isProfilePublic) setMakeProfilePublicDialog(true);
                      else {
                          setEditDialogOpen(true);
                          setHideThumbnail(true);
                      }
                  }
                : null,
        },
        editDialog: {
            // data: data,
            open: editDialogOpen,
            onClose: handleCloseEditDialog.bind(null, setEditDialogOpen, isPlayedVideo, setHideThumbnail),
            videoOverviewUrl: _get(props, 'data.videoOverviewUrl'),
            videoOverviewKey: _get(props, 'data.videoOverviewKey'),
            videoOverviewName: _get(props, 'data.videoOverviewName'),
            userAvatar,
            // hideThumbnail,
        },
        videoBlock: {},
        videoOverviewBlock: {
            videoOverviewUrl: _get(props, 'data.videoOverviewUrl', ''),
            onPlayVideo: () => {
                setIsPlayedVideo(true);
                setHideThumbnail(true);
            },
            hideThumbnail,
        },
        existingVideoFilenameText: {
            variant: 'body1',
        },
        existingVideoFileSizeText: {
            variant: 'body2',
        },
        makeProfilePublicDialog: {
            maxWidth: 'sm',
            title: 'Edit Your Profile?',
            onClose: () => setMakeProfilePublicDialog(false),
            onConfirm: onConfirmChangeProfileType,
            open: makeProfilePublicDialog,
            message: CONFIRM_DIALOG_MESSAGE_CHANGE_JS_PROFILE_TO_DRAFT,
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
// const handleClickRecordVideoLink = (setEditDialogOpen, evt) => {
//     setEditDialogOpen(true);
// };

const handleCloseEditDialog = (setEditDialogOpen, isPlayedVideo, setHideThumbnail, evt) => {
    setEditDialogOpen(false);
    if (!isPlayedVideo) setHideThumbnail(false);
};

const videoExists = props => {
    return !_isEmpty(_get(props, 'data.videoOverviewUrl'));
};

const getVideoOverviewBlock = ({ props, defns, videoOverviewPlayerRef }) => {
    if (!videoExists(props)) {
        console.log('%c@@@@ getVideoOverviewBlock INSIDE IF', 'background: #222; color: #bada55');

        return (
            <div {...defns.videoPlaceholder}>
                {/* <div><VideocamIcon {...defns.videocamIcon} style={{ fontSize: '70px' }} /></div> */}
                <div>
                    <VideocamIcon {...defns.videocamIcon} />
                </div>
                <div>
                    <AfitdTypography {...defns.recordVideoText} />
                </div>
            </div>
        );
    } else {
        console.log('%c@@@@ getVideoOverviewBlock INSIDE ELSE', 'background: #222; color: #bada55');
        const videoOverviewName = _get(props, 'data.videoOverviewName');
        const videoOverviewSize = _get(props, 'data.videoOverviewSize');
        const { size: videoOverviewSizeText, unit: videoOverviewUnitText } = convertFileSize(videoOverviewSize);
        const videoOverviewDuration = _get(props, 'data.videoOverviewDuration');
        const videoOverviewDurationText = getVideoOverviewDurationTextBySeconds(videoOverviewDuration);

        return (
            <div {...defns.existingVideoInfoContainer}>
                <div {...defns.existingVideoNameSizeContainer}>
                    <AfitdTypography {...defns.existingVideoFilenameText}>{videoOverviewName}</AfitdTypography>
                    <AfitdTypography {...defns.existingVideoFileSizeText}>{`${videoOverviewSizeText}${videoOverviewUnitText}`}</AfitdTypography>
                </div>
                <div>
                    <AfitdTypography {...defns.existingVideoDurationText}>{videoOverviewDurationText}</AfitdTypography>
                </div>
            </div>
        );
    }
    // } else {

    //     console.log('%c@@@@ getVideoOverviewBlock INSIDE ELSE', 'background: #222; color: #bada55');
    //     console.log('%c@@@@ getVideoOverviewBlock INSIDE ELSE', 'background: #222; color: #bada55');
    //     console.log('%c@@@@ getVideoOverviewBlock INSIDE ELSE', 'background: #222; color: #bada55');
    //     console.log('%c@@@@ getVideoOverviewBlock INSIDE ELSE', 'background: #222; color: #bada55');

    //     return (
    //         <VideoOverviewBlock {...defns.videoOverviewBlock} />
    //     );
    // }

    // var videoid =document.getElementsByClassName("vjs-control-bar");
    // videoid[0].style.setProperty("display", "none", "important");

    // return (
    //     <VideoOverviewBlock {...defns.videoOverviewBlock} />
    // );

    // return (
    //     <div>
    //         <VideocamIcon {...defns.videocamIcon} style={{ fontSize: '70px' }} />
    //         <AfitdTypography {...defns.recordVideoText} />
    //     </div>
    // );
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const VideoOverview = props => {
    // console.log({ props });
    // console.log('%c@@@@ VideoOverview START', 'background: #222; color: #bada55');
    // console.log('%c@@@@ VideoOverview START', 'background: #222; color: #bada55');
    // console.log('%c@@@@ VideoOverview START', 'background: #222; color: #bada55');
    // console.log('%c@@@@ VideoOverview props=', 'background: #222; color: #bada55', props);

    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const { userId, currentUser } = useContext(AppContext);

    const { setSnackbarMessage, setSnackbarErrorMessage } = useContext(LayoutContext);
    const videoOverviewPlayerRef = useRef(null);

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    // const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [makeProfilePublicDialog, setMakeProfilePublicDialog] = useState(false);
    const [isPlayedVideo, setIsPlayedVideo] = useState(false);
    const [hideThumbnail, setHideThumbnail] = useState(false);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const isVideoExist = videoExists(props);
    const isVideoProcessing = _get(props, 'data.isVideoProcessing');
    const isVideoFailedTest = _get(props, 'data.isVideoFailedTest');
    const badContentDetected = _get(props, 'data.badContentDetected');

    const { editDialogOpen, setEditDialogOpen } = props;

    // const videoOverviewName = _get(props, 'data.videoOverviewName');
    // const videoOverviewSize = _get(props, 'data.videoOverviewSize');

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Handlers
    //---------------------------------------------------------------------
    const onConfirmChangeProfileType = async () => {
        await changeProfileDraft({ userId, setSnackbarMessage, setSnackbarErrorMessage });
    };

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const classes = useStyles({ isVideoExist, isVideoProcessing })();
    const defns = getDefns({
        classes,
        props,
        editDialogOpen,
        setEditDialogOpen,
        makeProfilePublicDialog,
        setMakeProfilePublicDialog,
        onConfirmChangeProfileType,
        hideThumbnail,
        setHideThumbnail,
        isPlayedVideo,
        setIsPlayedVideo,
        userAvatar: currentUser?.profilePicUrl,
        isVideoProcessing,
    });
    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <div {...defns.header}>
                <div {...defns.titleContainer}>
                    <AfitdTypography {...defns.titleText} />
                </div>
                <div {...defns.recordButtonContainer}>
                    <AfitdLink {...defns.recordVideoLink} />
                </div>
            </div>
            <div {...defns.hintContainer}>
                <AfitdTypography {...defns.hintText}>
                    Don't miss your shot to show the world what you've got! Adding a video to your profile is optional. But if you do, creating a high quality video that highlights
                    your best qualities and expertise is advantageous. Your video will not be seen by employers until your profile is made public, so record as many takes as you
                    need, no worries. Now's your chance to stand out! Need tips? View our article in the resource center <AfitdLink {...defns.resourceCenterLink}>here</AfitdLink>.
                    Want to see an example? We have some that may help <AfitdLink {...defns.videoExampleLink}>here</AfitdLink>.
                </AfitdTypography>
            </div>
            <FieldLabel>Video:</FieldLabel>
            {isVideoProcessing || isVideoFailedTest ? (
                <div {...defns.processingContainer}>
                    <InfoOutlinedIcon {...defns.infoIcon} />
                    &nbsp;&nbsp; {isVideoFailedTest ? `We detected ${badContentDetected} in your video` : 'Your Video is processing, please check back later!'}
                </div>
            ) : (
                <div {...defns.videoBlock}>
                    {getVideoOverviewBlock({ props, defns, videoOverviewPlayerRef })}
                    <VideoOverviewBlock {...defns.videoOverviewBlock} userAvatar={currentUser?.profilePicUrl} />
                </div>
            )}
            <AfitdConfirmDialog {...defns.makeProfilePublicDialog} />
            {editDialogOpen && <VideoOverviewDialog {...defns.editDialog} />}
        </div>
    );
};

export default VideoOverview;
